import React, { useEffect, useState, useContext, useRef } from 'react';
import { AuthContext } from "../contexts/auth";
import TimeRangeSelect from './time_range_select';
import { getDayCalendarWorkingHour, getEmployeesWithActiveContract } from '../services/api_calendar';
import { getHolidaysCompanyList } from '../services/api_holiday';
import { getMonthBlocked } from '../services/api_months_blocked';
import { Link } from 'wouter';
import Swal from 'sweetalert2';
import Fetching from './fetching';
import moment from 'moment';
import NoPermissions from '../authorization/NoPermissions';
import DayDetailsModal from './day_details_modal';

const EmployeeAttendanceTable = ({
    selectedUnit,
    employeeIds = [],
    page,
    pageSize,
    setTotalResult,
    setNextPage,
    setPrevPage,
    onAbortControllerReady,
    date,
}) => {
    const { token, profile } = useContext(AuthContext);

    const [dipendenti, setDipendenti] = useState([]);
    const [selectedTimeRange, setSelectedTimeRange] = useState('day');

    const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
    const [isErrorEmployees, setIsErrorEmployees] = useState(false);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);

    const [employeeCalendarDailyWh, setEmployeeCalendarDailyWh] = useState(null);
    const [employeeShift, setEmployeeShift] = useState([]);

    const [allHolidays, setAllHolidays] = useState([]);
    const [holidayToShow, setHolidayToShow] = useState([]);
    const [isForbidden, setIsForbidden] = useState(false);
    const [loadTime, setLoadTime] = useState(null);

    const [isModalDetailsOpen, setIsModalDetailsOpen] = useState(false);
    const [dayDetailsData, setDayDetailsData] = useState(null);
    const [employeeDetails, setEmployeeDetails] = useState('');

    const controller = useRef(null);
    const isEmployee = profile?.customer_type === 'employee';
    const [blockedMonths, setBlockedMonths] = useState([]);

    // Tick caricamento
    useEffect(() => {
        setLoadTime(moment());
    }, []);

    // Festività (fetch una volta sola)
    useEffect(() => {
        let cancelled = false;
        (async () => {
        try {
            setIsLoading(true);
            const holidaysResponse = await getHolidaysCompanyList(token, 'attendance');
            if (!cancelled && Array.isArray(holidaysResponse)) setAllHolidays(holidaysResponse);
            setIsLoading(false);
        } catch (error) {
            console.error('Errore festività:', error);
            setIsError(error?.response?.status === 402);
            setIsLoading(false);
        }
        })();
        return () => { cancelled = true; };
    }, [token]);

    // Filtra festività per giorno corrente (usa 'date', non 'selectedDate')
    useEffect(() => {
        if (!allHolidays.length) { setHolidayToShow([]); return; }
        const d = new Date(date);
        const day = d.getDate();
        const month = d.getMonth() + 1;
        setHolidayToShow(allHolidays.filter(h => h.day === day && h.month === month));
    }, [date, allHolidays]);

    const options = { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' };
    const formattedDate = new Intl.DateTimeFormat('it-IT', options).format(date);

    // Fetch dati tabella per la data corrente (usa 'date' ovunque)
    const fetchData = async () => {
        controller.current?.abort();
        controller.current = new AbortController();
        const signal = controller.current.signal;
        onAbortControllerReady?.(controller.current);

        try {
        const todayISO = moment(date).format('YYYY-MM-DD');

            setIsLoadingEmployees(true);
            setIsErrorEmployees(false);
            setIsForbidden(false);

            const calendarResponse = await getDayCalendarWorkingHour(
                token,
                todayISO,
                selectedUnit?.id ?? null,
                employeeIds,
                page,
                pageSize,
                signal,
                'attendance'
            );

            const arrayCalendar = Array.isArray(calendarResponse?.results) ? calendarResponse.results : [];

            setTotalResult(calendarResponse?.count ?? 0);
            setNextPage(calendarResponse?.next ?? null);
            setPrevPage(calendarResponse?.previous ?? null);

            const hasManageJustificationPermission =
                profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
                profile.employee?.permissions?.includes('hr.manage_justification_all');

            const hasViewTimbraturePermission =
                profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
                profile.employee?.permissions?.includes('hr.view_inouts_all') ||
                profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
                profile.employee?.permissions?.includes('hr.manage_inouts_all');

            const modified = arrayCalendar.map(entry => ({
                ...entry,
                days: entry.days.map(day => ({
                ...day,
                justification_requests:
                    isEmployee && !hasManageJustificationPermission ? [] : day.justification_requests,
                inouts:
                    isEmployee && !hasViewTimbraturePermission ? [] : day.inouts,
                has_active_contract: day.has_active_contract,
                })),
            }));

            setDipendenti(
                modified.map(e => ({
                first_name: e.first_name,
                last_name: e.last_name,
                cf: e.cf,
                id: e.id,
                has_active_contract: e?.days?.[0]?.has_active_contract,
                }))
            );

            const employeeWorkingHoursData = modified.flatMap(entry =>
                entry.days.map(day => ({
                employeeId: entry.id,
                date: day.date,
                typology: entry.typology,
                week_hours: day.week_hours,
                expected_week_hours: day.expected_week_hours,
                shift_type: day.shift_type,
                has_active_contract: day.has_active_contract,
                holidays: day.holidays,
                justification_requests: day.justification_requests || [],
                inouts: day.inouts || [],
                shifts: (day.shifts || []).map(shift => ({
                    ref_id: shift.ref_id,
                    ref_name: shift.ref_name,
                    ref_ou: shift.ref_ou,
                    daily_working_hour: shift.daily_working_hour,
                    time_slots: (shift.time_slots || []).map(slot => ({ ...slot, date: day.date })),
                })),
                }))
            );

            setEmployeeCalendarDailyWh(modified);
            setEmployeeShift(employeeWorkingHoursData);
            setIsLoadingEmployees(false);

            const blockedMonthsResponse = await getMonthBlocked(token, { startDate: todayISO, endDate: todayISO }, 'attendance');
            setBlockedMonths(Array.isArray(blockedMonthsResponse) ? blockedMonthsResponse : []);
        } catch (error) {
            if (error?.response?.status === 403) {
                setIsForbidden(true);
            } else if (error?.name !== 'CanceledError') {
                console.error(error);
                setIsErrorEmployees(true);
                setIsLoadingEmployees(false);
            } else if (error?.response?.status !== 402) {
                setIsErrorEmployees(false);
            }
        }
    };

    useEffect(() => {
        fetchData();
        return () => controller.current?.abort();
    }, [token, date, page, pageSize, selectedUnit, employeeIds]);

    const isDayBlocked = (d) =>
        blockedMonths.some(m => Number(m.year) === d.getFullYear() && Number(m.month) === d.getMonth() + 1);

    // Usa 'date' qui
    const handleSwalMessage = (employeeId, hour, minute) => {
        if (!employeeCalendarDailyWh || !hour || minute == null) return;

        const selectedDay = moment(date).format('YYYY-MM-DD');
        const employeeShift = employeeCalendarDailyWh.find(
        (entry) => entry.id === employeeId && entry.days.some((d) => d.date === selectedDay)
        );
        if (!employeeShift) return;

        const dayShifts = employeeShift.days.find((d) => d.date === selectedDay);
        if (!dayShifts) return;

        const allJustifications = (dayShifts.justification_requests ?? []).map((j) => {
        const justificationFrom = moment(j.datetime_from);
        const justificationTo = moment(j.datetime_to);
        if (justificationTo.format('HH:mm') === '00:00') justificationTo.subtract(1, 'minute');
        return {
            ...j,
            startHour: justificationFrom.hour(),
            startMinute: justificationFrom.minute(),
            endHour: justificationTo.hour(),
            endMinute: justificationTo.minute(),
            justificationFrom,
            justificationTo,
        };
        }).sort((a, b) => (a.startHour - b.startHour) || (a.startMinute - b.startMinute));

        const [currentHour] = hour.split(':').map(Number);

        const isInTimeSlot = (dayShifts.time_slots || []).some((shift) => {
        const shiftStart = moment(`1970-01-01T${shift.start_at}:00`);
        const shiftEnd = moment(`1970-01-01T${shift.end_at === '24:00' ? '23:59' : 'shift.end_at'}:00`);
        const sh = shiftStart.hour(), sm = shiftStart.minute(), eh = shiftEnd.hour(), em = shiftEnd.minute();
        return (currentHour > sh || (currentHour === sh && minute >= sm)) &&
                (currentHour < eh || (currentHour === eh && minute <= em));
        });

        const isInJustifications = allJustifications.some(j =>
        (currentHour > j.startHour || (currentHour === j.startHour && minute >= j.startMinute)) &&
        (currentHour < j.endHour || (currentHour === j.endHour && minute <= j.endMinute))
        );

        if (!isInTimeSlot && !isInJustifications) {
        return;
        }

        let htmlContent = `
        <div>
            ${dayShifts.time_slots.map((shift) => {
            const shiftStart = moment(`1970-01-01T${shift.start_at}:00`);
            const shiftEnd = moment(`1970-01-01T${shift.end_at}:00`);

            return `
                <p style="padding-bottom: 8px;">
                <strong>Fascia oraria lavoro:</strong> ${shift.start_at} - ${shift.end_at}
                </p>
                ${shift.breaks && shift.breaks.length > 0 ? `
                <p style="padding-top: 2px; color: #9ca3af;"><strong>Pause</strong></p>
                <ul style="padding-bottom: 8px;">
                    ${shift.breaks.map((breakSlot) => `
                    <li>${breakSlot.start_at} - ${breakSlot.end_at}</li>
                    `).join('')}
                </ul>
                <hr style="border-top: 1px solid #ccc; margin-bottom: 8px;">
                ` : ''}
            `;
            }).join('')}

            ${(allJustifications.length > 0) ? `
            <p style="padding-top: 10px; color: #FB9123;"><strong>Giustificativi:</strong></p>
            <ul>
                ${allJustifications.map((justification) => {
                const justificationFromFormatted = justification.justificationFrom.format('HH:mm');
                const justificationToFormatted = justification.justificationTo.format('HH:mm');
                const justificationDateFrom = justification.justificationFrom.format('DD/MM');
                const justificationDateTo = justification.justificationTo.format('DD/MM');
                // Condizione per "Intera giornata"
                if (justificationFromFormatted === '00:00' && justificationToFormatted === '23:59') {
                    return `<li>${justification.justification.justification.description}: Intera giornata</li>`;
                }
                // Condizione per giorni diversi
                if (justificationDateFrom !== justificationDateTo) {
                    return `<li>${justification.justification.justification.description}: dalle ore ${justificationFromFormatted} del ${justificationDateFrom} alle ore ${justificationToFormatted} del ${justificationDateTo}</li>`;
                }
                // Default: mostra orari e data (per giustificativi nello stesso giorno)
                return `<li>${justification.justification.justification.description}: dalle ore ${justificationFromFormatted} alle ore ${justificationToFormatted}</li>`;
                }).join('')}
            </ul>
            ` : ''}
        </div>
        `;

        Swal.fire({
        title: "Dettagli del Turno",
        html: htmlContent,
        });
    };

    const handleOpenDayDetails = (employee, dayObj) => {
        const dayDate = moment(dayObj.date).format('YYYY-MM-DD');
        setEmployeeDetails(employee);
        setDayDetailsData(dayDate);
        setIsModalDetailsOpen(true);
    };

    const refetchEmployeeData = async (employeeId) => {
        try {
            const todayISO = moment(date).format('YYYY-MM-DD');
            const calendarResponse = await getDayCalendarWorkingHour(
                token, todayISO, null, [employeeId], 1, 1, null, 'attendance'
            );
            if (!calendarResponse?.results?.length) return;
            const updatedEmployeeData = calendarResponse.results[0];

            const hasManageJustificationPermission =
                profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
                profile.employee?.permissions?.includes('hr.manage_justification_all');

            const hasViewTimbraturePermission =
                profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
                profile.employee?.permissions?.includes('hr.view_inouts_all') ||
                profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
                profile.employee?.permissions?.includes('hr.manage_inouts_all');

            const sanitized = {
                ...updatedEmployeeData,
                days: updatedEmployeeData.days.map(day => ({
                ...day,
                justification_requests: isEmployee && !hasManageJustificationPermission ? [] : day.justification_requests,
                inouts: isEmployee && !hasViewTimbraturePermission ? [] : day.inouts,
                has_active_contract: day.has_active_contract,
                })),
            };

            setEmployeeCalendarDailyWh(prev => prev.map(emp => (emp.id === employeeId ? sanitized : emp)));
        } catch (e) {
            console.error('Error refetching employee data:', e);
        }
    };

    const refetchEmployeeDay = async (employeeId, dateISO) => {
        try {
            const calendarResponse = await getDayCalendarWorkingHour(
                token, dateISO, selectedUnit?.id, [employeeId], 1, 1, null, 'attendance'
            );
            if (!calendarResponse?.results?.length) return;

            const updated = calendarResponse.results[0];
            const hasManageJustificationPermission =
                profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
                profile.employee?.permissions?.includes('hr.manage_justification_all');
            const hasViewTimbraturePermission =
                profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
                profile.employee?.permissions?.includes('hr.view_inouts_all') ||
                profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
                profile.employee?.permissions?.includes('hr.manage_inouts_all');

            const sanitized = {
                ...updated,
                days: updated.days.map(day => ({
                ...day,
                justification_requests: isEmployee && !hasManageJustificationPermission ? [] : day.justification_requests,
                inouts: isEmployee && !hasViewTimbraturePermission ? [] : day.inouts,
                has_active_contract: day.has_active_contract,
                })),
            };

            setEmployeeCalendarDailyWh(prev =>
                Array.isArray(prev) ? prev.map(emp => (emp.id === employeeId ? sanitized : emp)) : [sanitized]
            );
            setLoadTime(moment());
        } catch (e) {
            console.error('Refetch day failed', e);
        }
    };

    if (isForbidden) return <NoPermissions />;
    if (isLoadingEmployees) return <div className='w-full'><Fetching /></div>;
    if (isErrorEmployees) return <div>Error loading data employee</div>;
    if (isLoading) return <div className='w-full'><Fetching /></div>;
    if (isError) return <div>Error loading data</div>;

    const dayTimeHours = ['06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00'];
    const nightTimeHours = ['22:00','23:00','00:00','01:00','02:00','03:00','04:00','05:00'];
    const filteredHours =
        selectedTimeRange === 'day'  ? dayTimeHours :
        selectedTimeRange === 'night'? nightTimeHours :
        ['00:00','01:00','02:00','03:00','04:00','05:00','06:00','07:00','08:00','09:00','10:00','11:00','12:00','13:00','14:00','15:00','16:00','17:00','18:00','19:00','20:00','21:00','22:00','23:00'];

    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');

    const hasViewTimbraturePermission =
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');

    const isHolidayDay = Array.isArray(holidayToShow) && holidayToShow.length > 0;
    
    return (
        <>
            <div className='bg-white rounded-md'>
                <div className="border-x border-gray-200 bg-gray-100">
                    <div className="px-4 py-4">
                        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                            <div className="min-w-0">
                                <h1 className="text-sm font-medium text-gray-800">
                                    PRESENZE GIORNO <span className="font-bold text-indigo-700 uppercase">{formattedDate}</span>
                                </h1>
                                {!!holidayToShow.length && (
                                <div className="mt-1 text-sm uppercase text-gray-700">
                                    Festività oggi:
                                    {holidayToShow.map((h, i) => (
                                    <span key={i} className="ml-2 font-semibold text-indigo-700">
                                        {h.name}{i < holidayToShow.length - 1 && ','}
                                    </span>
                                    ))}
                                </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 md:justify-end md:w-auto w-full">
                                <span className="text-[11px] uppercase tracking-wide text-gray-600">Seleziona orario</span>
                                <TimeRangeSelect
                                    selectedTimeRange={selectedTimeRange}
                                    handleTimeRangeChange={setSelectedTimeRange}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex items-center pt-4 pb-3 border-x border-gray-200 px-3 bg-gray-100">
                    <div className="w-3 h-3 bg-indigo-300 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>Orario lavorativo</span>
                    <div className="w-3 h-3 bg-gray-300 rounded-full mr-1"></div>
                    <span className='block font-medium text-gray-700 uppercase text-xs mr-3'>pausa</span>
                    {!(profile.customer_type === 'employee' && !hasManageJustificationPermission) && (
                        <>
                        <div className="w-3 h-3 bg-orange-400 rounded-full mr-1"></div>
                        <span className='block font-medium text-gray-700 uppercase text-xs'>giustificativi</span>
                        </>
                    )}
                    {!(profile.customer_type === 'employee' && !hasManageJustificationPermission) && (
                        <>
                        <div className="w-3 h-3 bg-sky-200 rounded-full ml-2 mr-1"></div>
                        <span className='block font-medium text-gray-700 uppercase text-xs'>turni da confermare</span>
                        </>
                    )}
                </div>
                <div className="flex items-center pb-3 border-x border-gray-200 px-3 bg-gray-100">
                    {!(profile.customer_type === 'employee' && !hasViewTimbraturePermission) && (
                        <>
                            <div
                                className="w-3 h-3 rounded-full mr-1"
                                style={{
                                background: 'linear-gradient(to right, #10B981 50%, #F87171 50%)'
                                }}
                            ></div>
                            <span className='block font-medium text-gray-700 uppercase text-xs'>timbratura</span> 
                            {loadTime && (
                                <span className='block text-gray-600 text-sm'> - aggiornata alle ore <span className='text-gray-800 font-semibold'>{loadTime.format('HH:mm:ss')}</span> del  <span className='text-gray-800 font-semibold'>{loadTime.format('DD/MM/YYYY')}</span></span>
                            )}
                        </>
                    )}
                </div>

                <div className="grid grid-cols-1">
                    <div className="overflow-x-auto" style={{ maxHeight: "40rem", overflowY: "scroll" }}>
                        {isLoading ? (
                            <div className='w-full'>
                                <Fetching />
                            </div>
                        ) : (
                            <div className='container overflow-auto' style={{ position: 'relative', maxHeight: '100%'}}>
                                <table className="min-w-max border divide-y divide-gray-200 w-full">
                                    <thead >
                                        <tr>
                                            <th className="pl-4 py-3 border-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"  style={{ position: 'sticky', top: 0, left: 0, zIndex: '9' }}>Dipendenti</th>
                                            {filteredHours.map(hour => (
                                                <th key={hour} className="py-3 sticky top-0 px-0.5 border-2 border-gray-200 bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase"
                                                style={{ zIndex: '5' }}>{hour}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {(() => {
                                        const employeesToShow = dipendenti;

                                        if (!isLoadingEmployees && employeesToShow.length === 0) {
                                            return (
                                            <tr>
                                                <td className="pl-4 py-3 border-2 border-gray-200 bg-white text-left italic text-sm font-medium text-gray-600" colSpan={filteredHours.length + 1}>Non sono presenti anagrafiche</td>
                                            </tr>
                                            );
                                        }

                                        return employeesToShow
                                            .map((dipendente, index) => {
                                            const dayData = (employeeCalendarDailyWh?.find((employee) => employee.id === dipendente.id)?.days || [])[0];

                                            return (
                                                <tr key={dipendente.id} className={index % 2 === 1 ? 'bg-gray-100' : ''}>
                                                    <td
                                                        className="sticky left-0 pl-4 py-3 border-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase" style={{ zIndex: '7' }}
                                                        title={dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}` : dipendente.cf}
                                                    >
                                                        <Link href={`/dipendenti/${dipendente.cf}`} className="hover:text-indigo-600">
                                                            {dipendente.first_name ? `${dipendente.last_name} ${dipendente.first_name}`.slice(0, 16) : dipendente.cf}
                                                            {dipendente.first_name && `${dipendente.last_name} ${dipendente.first_name}`.length > 16 ? '...' : ''}
                                                        </Link>
                                                    </td>

                                                    {filteredHours.map((hour) => {
                                                        const hourStartMoment = moment(date).set({ hour: parseInt(hour.split(':')[0], 10), minute: 0, second: 0, millisecond: 0 });
                                                        const hourEndMoment = hourStartMoment.clone().add(1, 'hour');

                                                        const colorForInout = (inout, dayData) => {
                                                            const overtime =
                                                            inout.over_time_checkin ||
                                                            inout.over_time_checkout ||
                                                            dayData?.inout?.over_time_checkin ||
                                                            dayData?.inout?.over_time_checkout;

                                                            return overtime ? "bg-red-400" : "bg-green-500";
                                                        };

                                                        const renderBar = (start, end, color, key, extraStyle = {}) => {
                                                            if (start.isSameOrAfter(hourEndMoment) || end.isSameOrBefore(hourStartMoment)) {
                                                                return null;
                                                            }

                                                            const intersectStart = moment.max(start, hourStartMoment);
                                                            const intersectEnd = moment.min(end, hourEndMoment);

                                                            const left = (intersectStart.minute() / 60) * 100;
                                                            const width = (intersectEnd.diff(intersectStart, 'minutes') / 60) * 100;

                                                            if (width <= 0) return null;

                                                            return (
                                                                <div
                                                                    key={key}
                                                                    className={`absolute h-full ${color}`}
                                                                    style={{ left: `${left}%`, width: `${width}%`, ...extraStyle }}
                                                                ></div>
                                                            );
                                                        };

                                                        return (
                                                            <td 
                                                                key={hour} 
                                                                className="border border-gray-200 text-center p-0 relative cursor-pointer"
                                                                style={dayData?.has_active_contract && isHolidayDay ? SvgHoliday() : undefined}
                                                            >
                                                                {dayData?.has_active_contract ? (
                                                                    <div className="relative w-full h-8 flex flex-col" onClick={() => handleOpenDayDetails(dipendente, dayData)}>
                                                                        {/* Orario di lavoro e Giustificativi */}
                                                                        <div className="absolute w-full h-6 flex mt-1">
                                                                            {dayData?.shifts?.flatMap(shift =>
                                                                                shift.time_slots.map(slot => {
                                                                                    const start = moment(slot.start_at, 'HH:mm').set({ year: hourStartMoment.year(), month: hourStartMoment.month(), date: hourStartMoment.date() });
                                                                                    const end = slot.end_at === '24:00'
                                                                                    ? start.clone().add(1, 'day').startOf('day')
                                                                                    : moment(slot.end_at, 'HH:mm').set({ year: hourStartMoment.year(), month: hourStartMoment.month(), date: hourStartMoment.date() });

                                                                                    if (end.isBefore(start)) {
                                                                                    end.add(1, 'day');
                                                                                    }

                                                                                    return renderBar(start, end, shift?.status === false ? 'bg-sky-200' : 'bg-indigo-200', `${slot.id}-shift`);
                                                                                })
                                                                            )}
                                                                            {dayData?.justification_requests
                                                                                ?.filter(justification => justification?.justification?.justification?.is_marker_area !== true)
                                                                                .map((justification, jIdx) => {
                                                                                const start = moment(justification.datetime_from);
                                                                                const end = moment(justification.datetime_to);

                                                                                const isPending = justification.status == null;
                                                                                  const keyBase =
                                                                                    justification.id != null
                                                                                    ? `just-${justification.id}`
                                                                                    : `just-${dayData?.date}-${dipendente.id}-${jIdx}-${start.valueOf()}-${end.valueOf()}`;


                                                                                if (isPending) {
                                                                                    return renderBar(start, end, "", `${keyBase}-pending`, PatternNullJustification());
                                                                                }
                                                         
                                                                                return renderBar(start, end, "bg-orange-400", `${keyBase}-ok`);

                                                                                
                                                                            })}
                                                                        </div>
                                                                        <div className="absolute w-full h-6 flex mt-1">
                                                                            {/* BREAKS */}
                                                                            {dayData?.shifts?.flatMap(shift =>
                                                                                shift.time_slots.flatMap(slot =>
                                                                                    (slot.breaks || []).map((brk, idx) => {
                                                                                        const bStart = moment(brk.start_at, 'HH:mm')
                                                                                        .set({ year: hourStartMoment.year(), month: hourStartMoment.month(), date: hourStartMoment.date() });

                                                                                        const bEndRaw = brk.end_at === '24:00'
                                                                                        ? bStart.clone().add(1, 'day').startOf('day')
                                                                                        : moment(brk.end_at, 'HH:mm')
                                                                                            .set({ year: hourStartMoment.year(), month: hourStartMoment.month(), date: hourStartMoment.date() });

                                                                                        const bEnd = bEndRaw.isBefore(bStart) ? bEndRaw.add(1, 'day') : bEndRaw;

                                                                                        const usePattern = brk.is_visible === false;

                                                                                        return renderBar(
                                                                                            bStart,
                                                                                            bEnd,
                                                                                            usePattern ? '' : 'bg-gray-300',
                                                                                            `${slot.id}-break-${idx}`,
                                                                                            usePattern ? PatternInvisibleBreak() : {}
                                                                                        );
                                                                                    })
                                                                                )
                                                                            )}
                                                                        </div>

                                                                        {/* Timbrature */}
                                                                        {!(profile.customer_type === 'employee' && !hasViewTimbraturePermission) && (
                                                                            <div className="absolute w-full h-2 flex top-[16px] -translate-y-1/2">
                                                                                {dayData?.inouts?.map((inout) => {
                                                                                    const start = moment(inout.checkin);
                                                                                    const end   = inout.checkout
                                                                                    ? moment(inout.checkout)
                                                                                    : moment();

                                                                                    const color = colorForInout(inout, dayData);

                                                                                    return renderBar(start, end, color, `${inout.id}-inout`);
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="bg-indigo-500 w-full text-transparent"
                                                                        style={{
                                                                            ...(PatternInactiveEmployee()),
                                                                        }}
                                                                    >
                                                                    .
                                                                    </div>
                                                                )}
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                            });
                                        })()}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {isModalDetailsOpen && (
                    <DayDetailsModal
                        isOpen={isModalDetailsOpen}
                        onClose={() => setIsModalDetailsOpen(false)}
                        formattedDay={dayDetailsData}
                        employee={employeeDetails}
                        isMonthOpen={!isDayBlocked(new Date(dayDetailsData))}
                        onAfterSaveTimbratura={(updatedTimbratura, oldDate) => {
                        setEmployeeCalendarDailyWh((prevData) =>
                            prevData.map((employee) => {
                            if (employee.id !== employeeDetails.id) return employee;

                            const updatedDays = employee.days.map((day) => {
                                if (day.date === oldDate && oldDate !== updatedTimbratura.checkin.split('T')[0]) {
                                return {
                                    ...day,
                                    inouts: day.inouts.filter(io => io.id !== updatedTimbratura.id)
                                };
                                }

                                if (day.date === updatedTimbratura.checkin.split('T')[0]) {
                                const exists = day.inouts.find(io => io.id === updatedTimbratura.id);
                                const updatedInouts = exists
                                    ? day.inouts.map(io => io.id === updatedTimbratura.id ? updatedTimbratura : io)
                                    : [...day.inouts, updatedTimbratura];

                                return { ...day, inouts: updatedInouts };
                                }

                                return day;
                            });

                            return { ...employee, days: updatedDays };
                            })
                        );
                        }}
                        onAfterDeleteTimbratura={(deletedTimbratura) => {
                        setEmployeeCalendarDailyWh((prevData) =>
                            prevData.map((employee) => {
                            if (employee.id !== employeeDetails.id) return employee;
                            return {
                                ...employee,
                                days: employee.days.map((day) => {
                                if (day.date !== deletedTimbratura.checkin.split('T')[0]) return day;
                                const updatedInouts = day.inouts.filter((inout) => inout.id !== deletedTimbratura.id);
                                return { ...day, inouts: updatedInouts };
                                }),
                            };
                            })
                        );
                        }}
                        onAfterSaveOrDeleteJustification={() => refetchEmployeeDay(employeeDetails.id, dayDetailsData)}
                        onOUChanged={() => refetchEmployeeData(employeeDetails.id, dayDetailsData)}
                        onShiftsChanged={() => refetchEmployeeDay(employeeDetails.id, dayDetailsData)}
                        holidayToShow={holidayToShow}
                    />
                )}
            </div>
        </>
    );
};

export default EmployeeAttendanceTable;

const PatternInactiveEmployee = () => {
  return {
    backgroundColor: '#ffffff',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%234f46e5' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
  };
};


const PatternInvisibleBreak = () => {
  return {
    backgroundColor: '#e5e7eb',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23374151' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
  };
};


const PatternNullJustification = () => {
  return {
    backgroundColor: '#fed7aa',
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='6' height='6' viewBox='0 0 6 6' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ea580c' fill-opacity='1' fill-rule='evenodd'%3E%3Cpath d='M5 0h1L0 6V5zM6 5v1H5z'/%3E%3C/g%3E%3C/svg%3E")`,
    backgroundRepeat: 'repeat',
  };
};


const SvgHoliday = () => {
    return {
        backgroundColor: '#ffffff',
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='48' height='34' viewBox='0 0 48 32' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fed7aa' fill-opacity='0.2'%3E%3Cpath d='M27 32c0-3.314 2.686-6 6-6 5.523 0 10-4.477 10-10S38.523 6 33 6c-3.314 0-6-2.686-6-6h2c0 2.21 1.79 4 4 4 6.627 0 12 5.373 12 12s-5.373 12-12 12c-2.21 0-4 1.79-4 4h-2zm-6 0c0-3.314-2.686-6-6-6-5.523 0-10-4.477-10-10S9.477 6 15 6c3.314 0 6-2.686 6-6h-2c0 2.21-1.79 4-4 4C8.373 4 3 9.373 3 16s5.373 12 12 12c2.21 0 4 1.79 4 4h2z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        backgroundRepeat: 'repeat',
    };
};